import React from 'react';
import Icon from '../../../components/AppIcon';

const PLAN_ICONS = {
  free: 'Zap',
  starter: 'Star',
  professional: 'Rocket',
  enterprise: 'Crown',
  custom: 'Settings'
};

const PLAN_COLORS = {
  free: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-700', icon: 'text-gray-500', selected: 'border-gray-400 bg-gray-50' },
  starter: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', icon: 'text-blue-500', selected: 'border-blue-500 bg-blue-50' },
  professional: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', icon: 'text-purple-500', selected: 'border-purple-500 bg-purple-50' },
  enterprise: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', icon: 'text-amber-500', selected: 'border-amber-500 bg-amber-50' },
  custom: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-700', icon: 'text-green-500', selected: 'border-green-500 bg-green-50' }
};

const formatPrice = (price) => {
  if (!price || price === 0) return 'Gratis';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 })?.format(price);
};

const PlanSelectionPanel = ({ plans, selectedPlanId, billingCycle, onPlanSelect, onBillingCycleChange, errors }) => {
  const getPrice = (plan) => {
    if (billingCycle === 'annual' && plan?.annual_price) return plan?.annual_price;
    if (billingCycle === 'quarterly' && plan?.quarterly_price) return plan?.quarterly_price;
    return plan?.monthly_price || 0;
  };

  const getDiscount = (plan) => {
    if (billingCycle === 'annual' && plan?.annual_price && plan?.monthly_price) {
      const monthly12 = plan?.monthly_price * 12;
      const discount = Math.round(((monthly12 - plan?.annual_price) / monthly12) * 100);
      return discount > 0 ? discount : null;
    }
    if (billingCycle === 'quarterly' && plan?.quarterly_price && plan?.monthly_price) {
      const monthly3 = plan?.monthly_price * 3;
      const discount = Math.round(((monthly3 - plan?.quarterly_price) / monthly3) * 100);
      return discount > 0 ? discount : null;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
          <Icon name="CreditCard" className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Plan de Suscripción</h2>
          <p className="text-sm text-gray-500">Elige el plan que mejor se adapte a tu taller</p>
        </div>
      </div>
      {/* Billing cycle toggle */}
      <div className="flex items-center justify-center">
        <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
          {[{ value: 'monthly', label: 'Mensual' }, { value: 'quarterly', label: 'Trimestral' }, { value: 'annual', label: 'Anual' }]?.map(cycle => (
            <button
              key={cycle?.value}
              type="button"
              onClick={() => onBillingCycleChange(cycle?.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                billingCycle === cycle?.value
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {cycle?.label}
              {cycle?.value === 'annual' && (
                <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">-20%</span>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {plans?.map(plan => {
          const tier = plan?.plan_tier || 'starter';
          const colors = PLAN_COLORS?.[tier] || PLAN_COLORS?.starter;
          const isSelected = selectedPlanId === plan?.id;
          const price = getPrice(plan);
          const discount = getDiscount(plan);
          const modules = Array.isArray(plan?.included_modules) ? plan?.included_modules : [];

          return (
            <button
              key={plan?.id}
              type="button"
              onClick={() => onPlanSelect(plan?.id)}
              className={`relative text-left p-5 rounded-xl border-2 transition-all hover:shadow-md ${
                isSelected
                  ? `${colors?.selected} shadow-md ring-2 ring-offset-1 ${
                      tier === 'professional' ? 'ring-purple-400' :
                      tier === 'enterprise' ? 'ring-amber-400' :
                      tier === 'starter' ? 'ring-blue-400' : 'ring-gray-400'
                    }`
                  : `${colors?.border} bg-white hover:${colors?.bg}`
              }`}
            >
              {isSelected && (
                <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <Icon name="Check" className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
              {discount && (
                <div className="absolute top-3 right-3">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                    -{discount}%
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors?.bg}`}>
                  <Icon name={PLAN_ICONS?.[tier] || 'Star'} className={`w-4 h-4 ${colors?.icon}`} />
                </div>
                <div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors?.badge}`}>
                    {plan?.plan_name}
                  </span>
                </div>
              </div>
              <div className="mb-3">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">{formatPrice(price)}</span>
                  {price > 0 && (
                    <span className="text-sm text-gray-500">
                      /{billingCycle === 'monthly' ? 'mes' : billingCycle === 'quarterly' ? 'trim.' : 'año'}
                    </span>
                  )}
                </div>
                {price === 0 && (
                  <p className="text-xs text-gray-500 mt-0.5">14 días de prueba incluidos</p>
                )}
              </div>
              <p className="text-xs text-gray-600 mb-3 line-clamp-2">{plan?.description}</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Icon name="Users" className="w-3.5 h-3.5 text-gray-400" />
                  <span>Hasta {plan?.max_users} usuario{plan?.max_users !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Icon name="Building" className="w-3.5 h-3.5 text-gray-400" />
                  <span>{plan?.max_branches} sucursal{plan?.max_branches !== 1 ? 'es' : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Icon name="FileText" className="w-3.5 h-3.5 text-gray-400" />
                  <span>{plan?.max_monthly_orders} órdenes/mes</span>
                </div>
                {modules?.slice(0, 3)?.map(mod => (
                  <div key={mod} className="flex items-center gap-2 text-xs text-gray-600">
                    <Icon name="Check" className="w-3.5 h-3.5 text-green-500" />
                    <span className="capitalize">{mod?.replace(/_/g, ' ')}</span>
                  </div>
                ))}
                {modules?.length > 3 && (
                  <p className="text-xs text-blue-600 font-medium">+{modules?.length - 3} módulos más</p>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {errors?.plan && (
        <p className="text-sm text-red-600 flex items-center gap-1.5">
          <Icon name="AlertCircle" className="w-4 h-4" />
          {errors?.plan}
        </p>
      )}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Icon name="Info" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Todos los planes incluyen <strong>14 días de prueba gratuita</strong>. Puedes cambiar de plan en cualquier momento desde tu panel de administración.
        </p>
      </div>
    </div>
  );
};

export default PlanSelectionPanel;
